import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import s from './NodesPage.module.css';
import { Card } from '@/components/primitives/Card';
import { Input } from '@/components/primitives/Input';
import { Spinner } from '@/components/primitives/Spinner';
import { StatusPill } from '@/components/primitives/StatusPill';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/Icon';
import { useTsNodes } from '@/api/tailscale/useTailscale';
import { relativeTime } from '@/lib/relativeTime';
import { NodeDetailSheet } from './NodeDetailSheet';
const PAGE_SIZE = 50;
/**
 * Headscale nodes list.
 *
 * Page size pinned to 50 (197 production nodes → 4 pages). No
 * virtualization yet — 50 rows render in <50ms on a 2020 laptop, no
 * need for the complexity. If a tailnet grows past ~1k nodes we'd
 * revisit.
 *
 * Tab + search are *client-side* filters because the upstream list
 * endpoint doesn't support either — we already fetch the full list for
 * dashboard counts, filtering locally is free.
 */
export function NodesPage() {
    const q = useTsNodes();
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState('all');
    const [page, setPage] = useState(1);
    const [searchParams, setSearchParams] = useSearchParams();
    // Deep-link target — TailscaleDashboard can link `/tailscale/nodes?focus=42`
    // to jump straight into a node's detail sheet.
    const focusId = searchParams.get('focus');
    const all = q.data?.nodes ?? [];
    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return all.filter((n) => {
            if (tab === 'online' && !n.online)
                return false;
            if (tab === 'offline' && n.online)
                return false;
            if (!term)
                return true;
            const haystack = [
                n.givenName,
                n.name,
                ...n.ipAddresses,
                ...n.forcedTags,
                ...n.validTags,
                n.user.name,
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [all, search, tab]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const countByTab = {
        all: all.length,
        online: all.filter((n) => n.online).length,
        offline: all.filter((n) => !n.online).length,
    };
    const openDetail = (id) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('focus', id);
            return next;
        });
    };
    const closeDetail = () => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('focus');
            return next;
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: s.header, children: _jsxs("div", { children: [_jsx("h1", { className: s.h1, children: "Nodes" }), _jsx("div", { className: s.h1Sub, children: q.isLoading
                                ? 'Loading…'
                                : `${all.length} node${all.length === 1 ? '' : 's'} · ${countByTab.online} online` })] }) }), _jsxs("div", { className: s.toolbar, children: [_jsxs("div", { className: s.search, children: [_jsx(Icon, { name: "search", size: 14 }), _jsx(Input, { placeholder: "Search hostname / IP / tag / user\u2026", value: search, onChange: (e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                } })] }), _jsx("div", { className: s.tabs, children: ['all', 'online', 'offline'].map((t) => (_jsxs("button", { type: "button", className: `${s.tab} ${tab === t ? s.tabActive : ''}`, onClick: () => {
                                setTab(t);
                                setPage(1);
                            }, children: [_jsx("span", { style: { textTransform: 'capitalize' }, children: t }), _jsx("span", { className: s.tabCount, children: countByTab[t] })] }, t))) })] }), q.isLoading && (_jsx(Card, { children: _jsxs("div", { className: s.placeholder, children: [_jsx(Spinner, { size: 14 }), " Loading nodes\u2026"] }) })), q.error && (_jsx(Card, { children: _jsx("div", { className: s.errBlock, children: q.error.message ?? 'Failed to load nodes' }) })), !q.isLoading && (_jsxs(Card, { className: s.list, children: [_jsxs("div", { className: s.rowHead, children: [_jsx("div", {}), _jsx("div", { children: "Hostname" }), _jsx("div", { children: "IP" }), _jsx("div", { children: "Tags" }), _jsx("div", { children: "Last seen" }), _jsx("div", { children: "Status" })] }), pageRows.map((n) => (_jsx(NodeRow, { n: n, onOpen: () => openDetail(n.id) }, n.id))), pageRows.length === 0 && (_jsx("div", { className: s.placeholder, children: search || tab !== 'all'
                            ? 'No nodes match the current filters.'
                            : 'No nodes yet.' })), totalPages > 1 && (_jsxs("div", { className: s.footer, children: [_jsxs("span", { className: s.footerInfo, children: ["Page ", safePage, " of ", totalPages, " \u00B7 ", filtered.length, " total"] }), _jsxs("div", { className: s.footerActions, children: [_jsx(Button, { variant: "ghost", onClick: () => setPage((p) => Math.max(1, p - 1)), disabled: safePage === 1, children: "\u2190 Prev" }), _jsx(Button, { variant: "ghost", onClick: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: safePage === totalPages, children: "Next \u2192" })] })] }))] })), _jsx(AnimatePresence, { children: focusId && (_jsx(NodeDetailSheet, { nodeId: focusId, onClose: closeDetail }, `detail-${focusId}`)) })] }));
}
function NodeRow({ n, onOpen }) {
    const tags = [...n.forcedTags, ...n.validTags.filter((t) => !n.forcedTags.includes(t))];
    return (_jsxs("div", { className: s.row, role: "button", tabIndex: 0, onClick: onOpen, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
            }
        }, children: [_jsx("div", { className: s.statusDot, "data-online": n.online ? 'true' : undefined, "aria-hidden": "true" }), _jsxs("div", { className: s.nameCell, children: [_jsx("div", { className: s.name, children: n.givenName || n.name }), _jsx("div", { className: s.subName, children: n.user.name })] }), _jsx("div", { className: s.ipCell, children: n.ipAddresses.map((ip) => (_jsx("div", { className: s.mono, children: ip }, ip))) }), _jsx("div", { className: s.tagCell, children: tags.length === 0 ? (_jsx("span", { className: s.muted, children: "\u2014" })) : (_jsxs("div", { className: s.tagList, children: [tags.slice(0, 3).map((t) => (_jsx("span", { className: s.tag, children: t }, t))), tags.length > 3 && (_jsxs("span", { className: s.muted, children: ["+", tags.length - 3] }))] })) }), _jsx("div", { className: s.lastSeen, children: n.online ? _jsx("span", { className: s.onlineNow, children: "now" }) : relativeTime(n.lastSeen) }), _jsx("div", { children: _jsx(StatusPill, { tone: n.online ? 'ok' : 'paused', children: n.online ? 'online' : 'offline' }) })] }));
}
